import React, { useState, useEffect } from "react";
import { useAuth } from "./Authentication/AuthContext";
import {
  updateUserDetails,
  getUserDetails,
  subscribeToJobs,
  unsubscribeFromJobs,
} from "../apis/UserApis";
import { useToastHelpers } from "./Toasts/ToastUtils";
import PreferencesSection from "./ProfilePreference";
import {
  FaUser,
  FaEnvelope,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaSave,
  FaBell,
  FaBellSlash,
  FaSpinner,
  FaCheck,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { useTheme } from "./Theme/ThemeContext";

export default function ProfilePage() {
  const { user, addUserDetails, setLoggedInUser } = useAuth();
  const { currentTheme } = useTheme();
  const [form, setForm] = useState({});
  const { successToast, errorToast } = useToastHelpers();
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    getUserDetails(user?.userId, user?.token)
      .then((data) => setForm(data))
      .catch((error) => console.error("Error fetching user details:", error));
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    try {
      const updatedUser = {
        ...form,
        age: parseInt(form.age, 10),
      };

      await updateUserDetails(user.userId, updatedUser, user.token);

      addUserDetails(updatedUser);
      successToast("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating user:", error);
      errorToast("Failed to update profile.");
    }
  };

  const getInitials = () => {
    const { firstName, lastName } = form || {};
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
  };

  const handleSubscriptionToggle = async () => {
    setSubscriptionLoading(true);
    try {
      if (form.isSubscribe) {
        await unsubscribeFromJobs(user.userId, user.token);
        setForm((prev) => ({ ...prev, isSubscribe: false }));
        setLoggedInUser((prev) => ({ ...prev, isSubscribe: false }));
        successToast("Unsubscribed from job notifications");
      } else {
        await subscribeToJobs(user.userId, user.token);
        setForm((prev) => ({ ...prev, isSubscribe: true }));
        setLoggedInUser((prev) => ({ ...prev, isSubscribe: true }));
        successToast("🎉 Subscribed to job notifications!");
      }
    } catch (error) {
      console.error("Subscription toggle error:", error);
      errorToast("Failed to update subscription. Please try again.");
    } finally {
      setSubscriptionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
      {/* Header / Hero */}
      <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} pt-20 pb-40 overflow-hidden`}>
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-400/20 rounded-full filter blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-400/10 rounded-full filter blur-3xl"></div>
        </div>

        <div className="relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-4">
            <FaUser className="w-4 h-4 text-white" />
            <span className="text-white text-sm font-medium">
              Account Settings
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            My Profile
          </h1>
          <p className="text-white/80 text-lg">
            Manage your personal information and preferences
          </p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-24 relative z-10 pb-12">
        <div className="glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 p-8 sm:p-10">
          {/* Avatar */}
          <div className="flex justify-center -mt-20 mb-8">
            <div className={`w-32 h-32 rounded-2xl bg-gradient-to-br ${currentTheme.colors.gradient} text-white flex items-center justify-center text-4xl font-bold border-4 border-white dark:border-neutral-800 shadow-xl ring-4 ring-white/30 dark:ring-white/10`}>
              {getInitials()}
            </div>
          </div>

          {/* User Name Display */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
              {form.firstName} {form.lastName}
            </h2>
            <p className="text-neutral-500 dark:text-neutral-400">{form.email}</p>
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="First Name"
              name="firstName"
              icon={<FaUser />}
              value={form.firstName}
              onChange={handleChange}
            />
            <InputField
              label="Last Name"
              name="lastName"
              icon={<FaUser />}
              value={form.lastName}
              onChange={handleChange}
            />
            <InputField
              label="Email"
              name="email"
              icon={<FaEnvelope />}
              value={form.email}
              readOnly
            />
            <InputField
              label="Age"
              name="age"
              type="number"
              icon={<FaCalendarAlt />}
              value={form.age}
              onChange={handleChange}
            />
            <div className="md:col-span-2">
              <InputField
                label="Address"
                name="address"
                icon={<FaMapMarkerAlt />}
                value={form.address}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={handleUpdate}
              className={`inline-flex items-center gap-2 bg-gradient-to-r ${currentTheme.colors.gradient} hover:opacity-90 text-white font-semibold py-3.5 px-8 rounded-xl transition-all duration-300 ${currentTheme.colors.glow} shadow-lg hover:shadow-xl hover:scale-[1.02]`}
            >
              <FaSave className="w-4 h-4" />
              Update Profile
            </button>
          </div>
        </div>

        {/* Job Subscription Section */}
        <div className="mt-8 glass rounded-3xl shadow-xl border border-white/20 dark:border-white/10 overflow-hidden">
          {/* Header with gradient */}
          <div className={`relative bg-gradient-to-r ${currentTheme.colors.gradient} p-6 overflow-hidden`}>
            {/* Decorative elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full filter blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-400/20 rounded-full filter blur-xl"></div>
            </div>

            <div className="relative flex items-center gap-4">
              <div className="w-12 h-12 bg-white/15 backdrop-blur-sm border border-white/20 rounded-xl flex items-center justify-center">
                <FaBell className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <HiSparkles className="w-4 h-4 text-yellow-300" />
                  <h3 className="text-lg font-bold text-white">
                    Email Notifications
                  </h3>
                </div>
                <p className="text-white/80 text-sm">
                  Manage your job alert preferences
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  {form.isSubscribe ? (
                    <span className="inline-flex items-center gap-1.5 bg-emerald-100/80 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold px-3 py-1 rounded-full">
                      <FaCheck className="w-3 h-3" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 bg-neutral-100/80 dark:bg-white/5 text-neutral-600 dark:text-neutral-400 text-xs font-semibold px-3 py-1 rounded-full">
                      <FaBellSlash className="w-3 h-3" />
                      Inactive
                    </span>
                  )}
                </div>
                <h4 className="text-neutral-900 dark:text-white font-semibold mb-1">
                  Job Alert Subscription
                </h4>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                  {form.isSubscribe
                    ? "You're receiving email notifications when new jobs are posted."
                    : "Get instant email alerts when new job opportunities matching your profile are posted."}
                </p>
              </div>

              {/* Toggle Switch */}
              <div className="flex items-center gap-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isSubscribe || false}
                    onChange={handleSubscriptionToggle}
                    disabled={subscriptionLoading}
                    className="sr-only peer"
                  />
                  <div
                    className={`relative w-14 h-7 bg-neutral-200 dark:bg-neutral-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-theme-primary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-neutral-600 peer-checked:bg-gradient-theme ${subscriptionLoading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                  >
                    {subscriptionLoading && (
                      <FaSpinner className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 animate-spin" />
                    )}
                  </div>
                </label>
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  {form.isSubscribe ? "Subscribed" : "Not Subscribed"}
                </span>
              </div>
            </div>

            {/* Info box */}
            <div className="mt-6 p-4 bg-accent-50/80 dark:bg-accent-900/20 border border-accent-100/50 dark:border-accent-800/50 rounded-xl backdrop-blur-sm">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <FaEnvelope className="w-5 h-5 text-accent-500 mt-0.5" />
                </div>
                <div>
                  <p className="text-sm text-accent-800 dark:text-accent-300">
                    <strong>How it works:</strong> When subscribed, you'll
                    receive an email notification whenever a new job,
                    internship, or referral opportunity is posted on CodeArena.
                    You can unsubscribe at any time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <PreferencesSection form={form} preferences={form.preferences} />
      </div>
    </div>
  );
}

function InputField({
  label,
  name,
  type = "text",
  value,
  onChange,
  readOnly = false,
  icon,
}) {
  return (
    <div>
      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
        {label}
      </label>
      <div className="relative">
        {icon && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500">
            {icon}
          </span>
        )}
        <input
          type={type}
          name={name}
          value={value || ""}
          readOnly={readOnly}
          onChange={onChange}
          className={`w-full ${icon ? "pl-11" : "pl-4"
            } pr-4 py-3 rounded-xl border text-neutral-800 dark:text-white transition-all ${readOnly
              ? "bg-neutral-100/50 dark:bg-white/5 border-neutral-200/50 dark:border-white/10 cursor-not-allowed"
              : "bg-neutral-50/50 dark:bg-white/5 border-neutral-200/50 dark:border-white/10 focus:bg-white dark:focus:bg-white/10 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 outline-none backdrop-blur-sm"
            }`}
        />
      </div>
    </div>
  );
}
